import type { ReactNode } from "react";

import styles from "./Checkbox.module.css";

interface CheckboxProps {
  checked: boolean;
  /** Reports the state the person asked for; the parent owns it. */
  onChange: (checked: boolean) => void;
  /** The label. It wraps the input, so it is the accessible name. */
  children: ReactNode;
  disabled?: boolean;
  /** On the `<label>`, next to the block's own class. */
  className?: string;
  id?: string;
  describedBy?: string;
}

/**
 * A check box drawn in anthracite with a tick that draws itself (BW-14).
 * The native input does the work — focus, Space, name, state — and the box
 * beside it is decoration (`aria-hidden`) that follows `:checked`.
 */
export function Checkbox({ checked, onChange, children, disabled, className, id, describedBy }: CheckboxProps) {
  return (
    <label className={className ? `${styles.check} ${className}` : styles.check}>
      <input
        type="checkbox"
        className={styles.input}
        id={id}
        checked={checked}
        disabled={disabled}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className={styles.box} aria-hidden="true">
        <svg viewBox="0 0 16 16" focusable="false">
          <path className={styles.tick} d="M3.5 8.5l3 3 6-7" pathLength="1" />
        </svg>
      </span>
      <span className={styles.text}>{children}</span>
    </label>
  );
}
